/**
 * Media Service - Handles media files and URLs with caching
 *
 * This service provides functionality for fetching media files and URLs,
 * with caching in the Redux store to reduce unnecessary API calls.
 */

import { cacheMediaFile } from '../store/slices/mediaSlice';
import { handleApiError, handleValidationError } from '../utils/errorHandling';

import { type MediaServiceInterface } from './interfaces/mediaServiceInterface';
import { mediaApiService } from './mediaApiService';
import { ServiceLocator } from './serviceLocator';

// Default TTL for media cache (24 hours) - 1 hour less than 24 hours to account
// for time to live on the presigned URL
// avoiding repeated audit logging of the same file
const DEFAULT_CACHE_TTL = 23 * 60 * 60 * 1000;

class MediaService implements MediaServiceInterface {
  /**
   * Sanitize a URL by removing any surrounding quotes
   *
   * @param url - The URL to sanitize
   * @returns The sanitized URL
   */
  sanitizeUrl(url: string): string {
    // Remove any surrounding double quotes
    return url.replace(/^"(.*)"$/, '$1');
  }

  /**
   * Get a media file with caching
   *
   * This function will check the Redux store cache first, and if the file is not in the cache
   * or has expired, it will fetch a new file and cache it.
   *
   * @param fileId - The ID of the file to get
   * @param ttlMs - Optional time to live in milliseconds for the cache entry
   * @returns A promise that resolves to the file URL
   */
  async getMediaFile(fileId: string, ttlMs?: number): Promise<string> {
    if (!fileId) {
      throw handleValidationError('fileId', 'getMediaFile');
    }

    // Get the current state
    const store = ServiceLocator.getStore();
    const state = store.getState();

    // Check if we have a cached file that's not expired
    const cachedFile = state.media.mediaFileCache[fileId];
    if (cachedFile && cachedFile.expiresAt > Date.now()) {
      console.log(`Using cached media file for ID: ${fileId}`);
      return cachedFile.data;
    }

    // If not in cache or expired, fetch a new file using the thunk
    console.log(`Fetching media file for ID: ${fileId}`);
    try {
      const url = await mediaApiService.getFilePresignedUrl(fileId);
      const sanitizedUrl = this.sanitizeUrl(url);

      store.dispatch(
        cacheMediaFile({
          fileId,
          data: sanitizedUrl,
          timestamp: Date.now(),
          expiresAt: Date.now() + (ttlMs || DEFAULT_CACHE_TTL),
        })
      );

      return sanitizedUrl;
    } catch (error) {
      throw handleApiError(error, 'getMediaFile', { fileId, ttlMs });
    }
  }
}

export const mediaService = new MediaService();
